import { createTheme } from "@mui/material/styles";

// Darwin design tokens (matches the tx darwin app's compiled CSS). Values are
// hex approximations of the Tailwind v4 oklch palette used there, plus the
// techex brand magenta. Used wherever MUI computes light/dark variants; raw
// oklch is also exposed in analyzer.css for places that don't go through MUI.
const DARWIN = {
  bg950: "#0a0f1e",  // background / surface-sunken
  bg900: "#11182a",  // card / surface (Paper)
  bg800: "#1c2438",  // muted / accent / surface-raised
  bg700: "#2a3349",
  bg600: "#384361",
  bg500: "#54607a",
  bg400: "#8d96a8",  // muted-foreground
  bg300: "#bdc4d2",
  bg200: "#dde2eb",
  bg100: "#eef1f5",
  bg50:  "#f7f9fb",  // foreground

  brand:   "#a01580", // brand-primary
  brandHover: "#b81a92",
  brandFg: "#f7f9fb",

  positive:    "#16a34a",
  warning:     "#f5b323",
  destructive: "#dc2626",
  error:       "#ef4444",
};

const theme = createTheme({
  palette: {
    mode: "dark",
    primary:   { main: DARWIN.brand, contrastText: DARWIN.brandFg },
    secondary: { main: DARWIN.bg700 },
    text:      { primary: DARWIN.bg50, secondary: DARWIN.bg400 },
    background:{ default: DARWIN.bg950, paper: DARWIN.bg900 },
    success:   { main: DARWIN.positive },
    warning:   { main: DARWIN.warning },
    error:     { main: DARWIN.destructive },
    divider:   "rgba(255,255,255,0.1)",
  },
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 600 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, letterSpacing: 0.1 },
    button: { fontWeight: 500 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { background: DARWIN.bg950, backgroundImage: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: DARWIN.bg900,
          backgroundImage: "none",
          backdropFilter: "none",
          border: "1px solid rgba(255,255,255,0.10)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { background: DARWIN.bg900, backgroundImage: "none", borderBottom: "1px solid rgba(255,255,255,0.08)" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 6, textTransform: "none", fontWeight: 500 },
        containedPrimary: {
          color: DARWIN.brandFg,
          backgroundColor: DARWIN.brand,
          backgroundImage: "none",
          boxShadow: "none",
          "&:hover": { backgroundColor: DARWIN.brandHover, boxShadow: "none" },
        },
        outlined: { borderColor: "rgba(255,255,255,0.15)", color: DARWIN.bg50 },
        text: { color: DARWIN.bg50 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: "rgba(255,255,255,0.03)",
          borderRadius: 6,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.25)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: DARWIN.bg500 },
          "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(160,21,128,0.25)" },
        },
        input: { paddingTop: 10, paddingBottom: 10 },
      },
    },
    MuiSelect: {
      styleOverrides: { icon: { color: DARWIN.bg50 }, select: { minHeight: 42 } },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 2, borderRadius: 0, backgroundColor: DARWIN.brand },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          minHeight: 44,
          color: DARWIN.bg400,
          "&.Mui-selected": { color: DARWIN.bg50 },
          "&:hover":         { color: DARWIN.bg100 },
        },
      },
    },
    MuiTableCell:    { styleOverrides: { head: { color: DARWIN.bg400 } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          color: DARWIN.bg400,
          borderColor: "rgba(255,255,255,0.15)",
          "&.Mui-selected": {
            backgroundColor: "rgba(255,255,255,0.10)",
            color: DARWIN.bg50,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.14)" },
          },
        },
      },
    },
  },
});

export default theme;
